/**
 * GlobalMetricsSection Component
 * Displays 6 main KPI cards: Voicebot metrics (cost, calls, avg) + Chatbot metrics (messages, leads, avg)
 */

import { KpiCard } from './KpiCard';
import { useAnalyticsSummary } from '@/hooks/useAnalytics';
import { Phone, MessageSquare, DollarSign, Users, TrendingUp, Mail } from 'lucide-react';
import type { AnalyticsDateRangeParams } from '@/types/analytics';

interface GlobalMetricsSectionProps {
  dateRange: AnalyticsDateRangeParams;
}

export function GlobalMetricsSection({ dateRange }: GlobalMetricsSectionProps) {
  const { data, isLoading, error } = useAnalyticsSummary(dateRange);

  if (error) {
    return (
      <div className="p-4 border border-destructive rounded-lg bg-destructive/10">
        <p className="text-sm text-destructive">Failed to load metrics: {error.message}</p>
      </div>
    );
  }

  // Format currency
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  // Format number with commas
  const formatNumber = (value: number): string => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {/* Voicebot Total Cost */}
      <KpiCard
        title="Voicebot Total Cost"
        value={isLoading ? '...' : formatCurrency(data?.voicebot.metrics.totalCost || 0)}
        subtitle={`${formatNumber(data?.voicebot.metrics.totalCalls || 0)} calls`}
        icon={DollarSign}
        loading={isLoading}
      />

      {/* Voicebot Total Calls */}
      <KpiCard
        title="Voicebot Total Calls"
        value={isLoading ? '...' : formatNumber(data?.voicebot.metrics.totalCalls || 0)}
        subtitle={`Avg duration: ${data?.voicebot.metrics.avgDuration?.toFixed(1) || '0'}s`}
        icon={Phone}
        loading={isLoading}
      />

      {/* Voicebot Avg Cost per Call */}
      <KpiCard
        title="Avg Cost per Call"
        value={isLoading ? '...' : formatCurrency(data?.voicebot.metrics.avgCostPerCall || 0)}
        subtitle="Voicebot efficiency"
        icon={TrendingUp}
        loading={isLoading}
      />

      {/* Chatbot Total Messages */}
      <KpiCard
        title="Chatbot Total Messages"
        value={isLoading ? '...' : formatNumber(data?.chatbot.metrics.totalMessages || 0)}
        subtitle={`${formatNumber(data?.chatbot.metrics.totalLeads || 0)} leads`}
        icon={MessageSquare}
        loading={isLoading}
      />

      {/* Chatbot Total Leads */}
      <KpiCard
        title="Chatbot Total Leads"
        value={isLoading ? '...' : formatNumber(data?.chatbot.metrics.totalLeads || 0)}
        subtitle={`Response rate: ${data?.chatbot.metrics.avgResponseRate?.toFixed(1) || '0'}%`}
        icon={Mail}
        loading={isLoading}
      />

      {/* Chatbot Avg Messages per Lead */}
      <KpiCard
        title="Avg Messages per Lead"
        value={isLoading ? '...' : (data?.chatbot.metrics.avgMessagesPerLead?.toFixed(1) || '0')}
        subtitle="Chatbot engagement"
        icon={Users}
        loading={isLoading}
      />

      {/* Combined section - full width */}
      <div className="md:col-span-2 lg:col-span-3 mt-4 p-4 border border-border rounded-lg bg-muted/30">
        <h3 className="text-sm font-semibold mb-3">Combined Overview</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <p className="text-xs text-muted-foreground">Total Interactions</p>
            <p className="text-xl font-bold">
              {isLoading ? '...' : formatNumber(data?.combined.totalInteractions || 0)}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Total Analyses</p>
            <p className="text-xl font-bold">
              {isLoading ? '...' : formatNumber(data?.combined.totalAnalyses || 0)}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Avg Sentiment Score</p>
            <p className="text-xl font-bold">
              {isLoading ? '...' : (data?.combined.avgSentimentScore?.toFixed(1) || '0')}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Cost per Interaction</p>
            <p className="text-xl font-bold">
              {isLoading ? '...' : formatCurrency(data?.combined.costPerInteraction || 0)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
